import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Tab, Tabs, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { AgendaScheduleTypes, AgendasTypes, IAgendasConfig, IAgenda, IPatient, OutpatientsVisualizationMode } from '../../constants/types';
import { PERMISSION } from '../../components/investigation/share/user_roles';
import { useAgendas } from '../../hooks/agenda';
import { getAgendasConfig } from '../../services/agenda';
import { getCurrentResearcherUuid } from '../../utils/index.jsx';
import Loader from '../../components/Loader';
import SectionHeader from '../components/SectionHeader';
import Appointments from './Outpatients/Appointments';
import { selectPersonalData } from './selectPersonalData';

interface MyScheduleAgendaViewProps {
    type: AgendasTypes;
    uuidInvestigation: string;
    agendasConfig: IAgendasConfig | null;
    myAgendas: IAgenda[];
    canCreateAppointments: boolean;
    personalData: IPatient[];
    selectedDate: Date;
}

const MyScheduleAgendaView: React.FC<MyScheduleAgendaViewProps> = ({
    type, uuidInvestigation, agendasConfig, myAgendas, canCreateAppointments, personalData, selectedDate
}) => {
    const uuidAgendas = useMemo(() => myAgendas.map(a => a.uuid), [myAgendas]);

    return (
        <Appointments
            uuidAgendas={uuidAgendas}
            typeAgenda={type}
            uuidInvestigation={uuidInvestigation}
            type={agendasConfig?.params[type]?.type ?? AgendaScheduleTypes.DATE_TIME}
            agendas={myAgendas}
            extraForm={agendasConfig?.params?.consultation?.extraForm}
            dateSelected={selectedDate}
            shouldReloadOnTabChange={true}
            mode={OutpatientsVisualizationMode.CONSULT}
            canCreateAppointments={canCreateAppointments}
            request={null}
            appointment={null}
            patientsPersonalData={personalData}
        />
    );
};

interface MyScheduleProps {
    investigations: any;
    personalData: IPatient[];
}

const MySchedule: React.FC<MyScheduleProps> = ({ investigations, personalData }) => {
    const { agendas: consultationAgendas, loadingAgendas: loadingConsultation } = useAgendas(AgendasTypes.CONSULTATION);
    const { agendas: surgeryAgendas, loadingAgendas: loadingSurgery } = useAgendas(AgendasTypes.SURGERY);
    const [agendasConfig, setAgendasConfig] = useState<IAgendasConfig | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

    // SHER-233: `Appointments`' fetch effect keys on the `dateSelected` prop
    // by identity, so calling `selectedDate.toDate()` inline in JSX handed it
    // a brand-new Date on every render and re-fetched the whole agenda from
    // the API each time. Memoise so the reference only changes when the user
    // actually picks a different date.
    const selectedDateAsDate = useMemo(() => selectedDate.toDate(), [selectedDate]);

    const currentUserUuid = useMemo(() => {
        try {
            return getCurrentResearcherUuid();
        } catch {
            return null;
        }
    }, []);

    const myConsultationAgendas = useMemo(() => {
        if (!consultationAgendas || !currentUserUuid) return [];
        return consultationAgendas.filter(
            (agenda: IAgenda) => agenda.principalResearcher?.uuid === currentUserUuid
        );
    }, [consultationAgendas, currentUserUuid]);

    const mySurgeryAgendas = useMemo(() => {
        if (!surgeryAgendas || !currentUserUuid) return [];
        return surgeryAgendas.filter(
            (agenda: IAgenda) => agenda.principalResearcher?.uuid === currentUserUuid
        );
    }, [surgeryAgendas, currentUserUuid]);

    const hasConsultation = myConsultationAgendas.length > 0;
    const hasSurgery = mySurgeryAgendas.length > 0;
    const hasBothTypes = hasConsultation && hasSurgery;

    const canCreateAppointments = useMemo(() => {
        if (investigations.currentInvestigation) {
            return investigations.currentInvestigation.permissions.includes(PERMISSION.CREATE_APPOINTMENTS);
        }
        return false;
    }, [investigations.currentInvestigation]);

    useEffect(() => {
        if (investigations.currentInvestigation) {
            getAgendasConfig(investigations.currentInvestigation.uuid)
                .then(response => {
                    setAgendasConfig(response.agendaConfig);
                })
                .catch(err => {
                    console.log(err);
                });
        }
    }, [investigations.currentInvestigation]);

    if (!investigations.currentInvestigation || loadingConsultation || loadingSurgery || consultationAgendas === null || surgeryAgendas === null) {
        return <Loader />;
    }

    if (!hasConsultation && !hasSurgery) {
        return (
            <Grid container spacing={3} justifyContent="center" style={{ marginTop: '2rem' }}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom>
                        <Translate id="pages.hospital.outpatients.consultation.title" />
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        <Translate id="pages.hospital.my-schedule.no-agendas" />
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const uuidInvestigation = investigations.currentInvestigation.uuid;

    const renderDatePicker = () => (
        <Grid item xs={12}>
            <DesktopDatePicker
                label="Date"
                value={selectedDate}
                onChange={(newDate: Dayjs | null) => {
                    if (newDate) setSelectedDate(newDate);
                }}
            />
        </Grid>
    );

    if (!hasBothTypes) {
        const type = hasConsultation ? AgendasTypes.CONSULTATION : AgendasTypes.SURGERY;
        const agendas = hasConsultation ? myConsultationAgendas : mySurgeryAgendas;
        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SectionHeader section={`outpatients.${type}`} edit={false} />
                </Grid>
                {renderDatePicker()}
                <Grid item xs={12}>
                    <MyScheduleAgendaView
                        type={type}
                        uuidInvestigation={uuidInvestigation}
                        agendasConfig={agendasConfig}
                        myAgendas={agendas}
                        canCreateAppointments={canCreateAppointments}
                        personalData={personalData}
                        selectedDate={selectedDateAsDate}
                    />
                </Grid>
            </Grid>
        );
    }

    const activeType = activeTab === 0 ? AgendasTypes.CONSULTATION : AgendasTypes.SURGERY;
    const activeAgendas = activeTab === 0 ? myConsultationAgendas : mySurgeryAgendas;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                    <Tab label={<Translate id="pages.hospital.outpatients.consultation.title" />} />
                    <Tab label={<Translate id="pages.hospital.outpatients.surgery.title" />} />
                </Tabs>
            </Grid>
            {renderDatePicker()}
            <Grid item xs={12}>
                <MyScheduleAgendaView
                    key={activeType}
                    type={activeType}
                    uuidInvestigation={uuidInvestigation}
                    agendasConfig={agendasConfig}
                    myAgendas={activeAgendas}
                    canCreateAppointments={canCreateAppointments}
                    personalData={personalData}
                    selectedDate={selectedDateAsDate}
                />
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state: any) => ({
    investigations: state.investigations,
    personalData: selectPersonalData(state),
});

export default connect(mapStateToProps, null)(MySchedule);
